import React, { useCallback, useEffect, useState } from 'react';
import { Col, Form, Tooltip, Typography } from 'antd';

import { LabelWithPlus } from '../components/LabelWithPlus';
import type { DynVarOption } from '../hooks/useDynamicVariables';
import { AgentVariableEditor } from './AgentVariableEditor';
import { CancelIcon, UndoIcon } from '@/assets/icons/icons';

const { Paragraph } = Typography;

function useResponsiveRows() {
  const calc = useCallback(() => {
    const w = window.innerWidth;
    if (w < 600) return 6;
    if (w < 900) return 12;
    return 13;
  }, []);

  const [rows, setRows] = useState<number>(() =>
    typeof window === 'undefined' ? 12 : calc()
  );

  useEffect(() => {
    const onResize = () => setRows(calc());
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [calc]);

  return rows;
}

export const BehaviorSection: React.FC<{
  onOpenDyn: () => void;
  dynVarOptions: DynVarOption[];
  onUndo?: () => void;
  canUndo?: boolean;
  onClearAll?: () => void;
  canClearAll?: boolean;
  isGenerating?: boolean;
  onCancelGenerate?: () => void;
  advancedMode?: boolean;
  onToggleAdvancedMode?: () => void;
}> = ({
  onOpenDyn,
  dynVarOptions,
  isGenerating = false,
  onUndo,
  canUndo,
  onClearAll,
  canClearAll,
  onCancelGenerate,
  advancedMode = false,
  onToggleAdvancedMode,
}) => {
  const rows = useResponsiveRows();

  return (
    <Col span={24} style={{ padding: 0 }}>
      <Form.Item
        name='behavior_instructions'
        rules={[
          { required: true, message: 'Behavior instructions are required' },
        ]}
        className='agentFormTextAreaItem'
        label={
          <div className='agentBehaviorHeaderRow'>
            <LabelWithPlus
              required={!advancedMode}
              text='Agent instructions'
              onPlusClick={onOpenDyn}
            />
            {isGenerating ? (
              <span
                role='button'
                tabIndex={0}
                onClick={onCancelGenerate}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') onCancelGenerate?.();
                }}
                style={{
                  cursor: 'pointer',
                  display: 'inline-flex',
                }}
                title='Cancel generation'
              >
                <CancelIcon />
              </span>
            ) : (
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  marginBottom: 4,
                }}
              >
                <Tooltip title='Advanced mode lets you skip instructions generation.'>
                  <button
                    type='button'
                    className={`agentInstrModal__advancedToggle ${
                      advancedMode
                        ? 'agentInstrModal__advancedToggle--active'
                        : ''
                    }`}
                    onClick={onToggleAdvancedMode}
                  >
                    Advanced
                  </button>
                </Tooltip>
                <span
                  role='button'
                  tabIndex={0}
                  onClick={() => canClearAll && onClearAll?.()}
                  onKeyDown={(e) => {
                    if (!canClearAll) return;
                    if (e.key === 'Enter' || e.key === ' ') onClearAll?.();
                  }}
                  style={{
                    opacity: canClearAll ? 1 : 0.35,
                    cursor: canClearAll ? 'pointer' : 'not-allowed',
                    height: 28,
                    padding: '0 12px',
                    borderRadius: 28,
                    fontSize: 12,
                    fontWeight: 600,
                    color: 'var(--text-primary)',
                    backgroundColor: 'var(--light-gray)',
                    display: 'inline-flex',
                    alignItems: 'center',
                  }}
                >
                  Clear all
                </span>
                <span
                  role='button'
                  tabIndex={0}
                  onClick={() => canUndo && onUndo?.()}
                  onKeyDown={(e) => {
                    if (!canUndo) return;
                    if (e.key === 'Enter' || e.key === ' ') onUndo?.();
                  }}
                  style={{
                    opacity: canUndo ? 1 : 0.35,
                    cursor: canUndo ? 'pointer' : 'not-allowed',
                    display: 'inline-flex',
                  }}
                >
                  <UndoIcon height={16} width={16} />
                </span>
              </div>
            )}
          </div>
        }
      >
        <AgentVariableEditor
          dynVarOptions={dynVarOptions}
          rows={rows}
          isGenerating={isGenerating}
        />
      </Form.Item>

      <Paragraph className='agentFormHelpText'>
        How the agent should behave and handle scenarios
      </Paragraph>
    </Col>
  );
};
